/**
 * RowFilterChips — composition filters for the payment matching queues.
 *
 * Single / Multiple / Partially Paid. Multi-select and OR-ed: picking Multiple + Partially Paid
 * asks for rows that are either, which is what "show me these kinds" means to a user.
 *
 * Counts are computed from the SAME predicates that do the filtering (ROW_FILTERS), against the
 * rows as they will be displayed — so a chip can never claim a count the table does not show. A
 * chip whose count is zero is disabled rather than hidden: a filter that vanishes when it would
 * return nothing leaves the user unsure whether it was ever there.
 */

import React from 'react';
import { Box, Chip } from '@mui/material';
import { ROW_FILTERS, rowFilterCounts } from '../../utils/paymentHelpers';

function RowFilterChips({ rows = [], active = [], onChange, sx }) {
  const keys = Object.keys(ROW_FILTERS);

  /* Counts are CONTEXTUAL: each is what that chip would yield given everything else selected, so
     the number predicts what clicking it does. Counting each filter standalone left
     "Partially Paid (2)" beside an active "Multiple" and then returned zero rows. */
  const counts = rowFilterCounts(rows, active);

  const toggle = (key) => {
    if (!onChange) return;
    if (active.includes(key)) {
      onChange(active.filter((k) => k !== key));
      return;
    }
    /* Single and Multiple are mutually exclusive — a row is one or the other, so selecting one
       REPLACES the other rather than adding to it. Each still clears back to "all" on a second
       click, so the pair behaves like radio buttons that can be switched off entirely. */
    if (ROW_FILTERS[key].group === 'composition') {
      onChange([...active.filter((k) => ROW_FILTERS[k]?.group !== 'composition'), key]);
      return;
    }
    onChange([...active, key]);
  };

  /* Nothing to filter between — every row is the same kind, so the chips would be noise.
     Evaluated only when NOTHING is selected, where the contextual counts equal the standalone ones;
     once a filter is active the bar must stay put, or the control the user just clicked would
     disappear underneath them. */
  const meaningful = keys.filter((k) => counts[k] > 0);
  if (meaningful.length < 2 && active.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', ...sx }}>
      {keys.map((key) => {
        const selected = active.includes(key);
        return (
          <Chip
            key={key}
            label={`${ROW_FILTERS[key].label} (${counts[key]})`}
            size="small"
            variant={selected ? 'filled' : 'outlined'}
            color={selected ? 'primary' : 'default'}
            onClick={() => toggle(key)}
            disabled={counts[key] === 0 && !selected}
          />
        );
      })}
      {active.length > 0 && (
        <Chip
          label="Clear"
          size="small"
          variant="outlined"
          onClick={() => onChange && onChange([])}
        />
      )}
    </Box>
  );
}

export default RowFilterChips;
